import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { PageHeader } from "../UsmLayout";
import { toast } from "sonner";
import { ScanLine, RotateCcw, AlertTriangle, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

/**
 * IADE — Sale Return / Void.
 *
 *  • RBAC gated at sales.manage (the route wrapper also enforces this).
 *  • Lookup by sale_no | EAN-8 | stock_code via GET /sales/find-for-return.
 *  • Confirmation step records the void via POST /sales/{id}/void
 *    — reuses the existing void engine, which:
 *      ✓ restores items back to active
 *      ✓ logs sale.voided audit + per-item inventory.unsold audit
 *      ✓ records sale_void stock movements (visible on the Phase 3 timeline)
 *
 *  No parallel return engine. All actions audited.
 */
const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });

export default function Return() {
  const navigate = useNavigate();
  const scanRef = useRef(null);
  const [code, setCode] = useState("");
  const [sale, setSale] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { scanRef.current?.focus(); }, []);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    const v = code.trim();
    if (!v) return;
    setBusy(true); setError(""); setSale(null);
    try {
      const r = await usmApi.get(`/sales/find-for-return?code=${encodeURIComponent(v)}`);
      setSale(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Satış bulunamadı";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const confirmReturn = async () => {
    if (!sale) return;
    if (!reason.trim()) { toast.error("Lütfen iade sebebini yazın"); return; }
    setConfirming(true);
    try {
      await usmApi.post(`/sales/${sale.id}/void`, { reason: reason.trim() });
      toast.success(`Satış ${sale.sale_no} iade edildi. Ürün(ler) stoğa geri alındı.`);
      navigate(`/usm/sales/${sale.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İade başarısız");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div data-testid="usm-return">
      <PageHeader
        title="İade"
        subtitle="Tamamlanmış satışı geri al ve ürün(leri) stoğa iade et"
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Satışlar", to: "/usm/sales" }, { label: "İade" }]}
        actions={
          <Link to="/usm/pos" data-testid="return-back-pos"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#71717A] px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9]">
            <ArrowLeft size={12} /> Ürün Satış
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] gap-4">
        {/* LEFT: lookup */}
        <section className="bg-white border border-[#E7E5E4] p-5 self-start">
          <form onSubmit={lookup}>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">
              Satış No · EAN-8 · Stok Kodu
            </label>
            <div className="relative">
              <ScanLine size={22} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C8A96A] pointer-events-none" />
              <input
                ref={scanRef}
                data-testid="return-code-input"
                value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="SAT000123 / 10001004 / UZG1000017"
                autoFocus autoComplete="off" spellCheck={false}
                className="w-full pl-12 pr-4 py-4 border border-[#E7E5E4] focus:border-[#C8A96A] focus:ring-2 focus:ring-[#C8A96A]/20 outline-none text-lg font-mono tracking-wide"
              />
              {busy && <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C8A96A] animate-spin" />}
            </div>
            <button
              data-testid="return-lookup-submit" type="submit" disabled={busy || !code.trim()}
              className="mt-3 w-full bg-[#0A0A0A] disabled:opacity-30 text-white text-xs uppercase tracking-[0.2em] py-3.5 min-h-[56px]"
            >
              {busy ? "Aranıyor…" : "Sorgula"}
            </button>
            {error && (
              <p data-testid="return-error" className="mt-3 px-3 py-2.5 bg-red-50 border border-red-100 text-red-700 text-sm">{error}</p>
            )}
          </form>

          <div className="mt-5 pt-5 border-t border-[#F5F5F4]">
            <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
              <AlertTriangle size={11} className="inline mr-1 text-amber-500" />
              Yalnızca <strong>tamamlanmış</strong> satışlar iade edilebilir. İptal işlemi geri alınamaz; ürünler stoğa <strong>active</strong> olarak döner ve satış <strong>voided</strong> olarak işaretlenir.
            </p>
          </div>
        </section>

        {/* RIGHT: sale details + confirmation */}
        <section className="bg-white border border-[#E7E5E4] min-h-[400px]">
          {!sale ? (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-10 text-[#A8A29E]">
              <RotateCcw size={36} className="text-[#E7E5E4] mb-3" />
              <p className="text-base">Bir satış sorgulayın.</p>
              <p className="text-sm mt-1">Sol panelden numara, barkod veya stok kodu girin.</p>
            </div>
          ) : (
            <div className="p-5 sm:p-6">
              <div className="flex flex-wrap items-baseline gap-3 mb-5 pb-4 border-b border-[#F5F5F4]">
                <h2 className="font-heading text-2xl">{sale.sale_no}</h2>
                <span className={
                  "px-2 py-0.5 text-[11px] uppercase tracking-[0.15em] ring-1 " +
                  (sale.status === "completed"
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-amber-50 text-amber-700 ring-amber-200")
                }>{sale.status}</span>
                <span className="ml-auto text-[12px] text-[#71717A] font-mono">{new Date(sale.created_at).toLocaleString("tr-TR")}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 text-[12.5px]">
                <KV label="Şube" value={sale.branch?.name || "—"} />
                <KV label="Çalışan" value={sale.employee?.full_name || sale.employee?.username || "—"} />
                <KV label="Müşteri" value={sale.customer_name || "—"} />
                <KV label="Telefon" value={sale.customer_phone || "—"} />
              </div>

              <div className="border border-[#F5F5F4] mb-5">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
                    <tr>
                      <th className="text-left px-3 py-2.5">Stok Kodu</th>
                      <th className="text-left px-3 py-2.5">Ürün</th>
                      <th className="text-right px-3 py-2.5">Adet</th>
                      <th className="text-right px-3 py-2.5">Birim</th>
                      <th className="text-right px-3 py-2.5">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sale.items || []).map((it) => (
                      <tr key={it.id} className="border-t border-[#F5F5F4]" data-testid={`return-item-${it.stock_code}`}>
                        <td className="px-3 py-2.5 font-mono text-[12.5px]">{it.stock_code}</td>
                        <td className="px-3 py-2.5">{it.product_name}</td>
                        <td className="px-3 py-2.5 text-right">{it.qty}</td>
                        <td className="px-3 py-2.5 text-right font-finance">{TRY.format(it.unit_price)}</td>
                        <td className="px-3 py-2.5 text-right font-finance">{TRY.format(it.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#FAFAF9] text-sm">
                    <tr>
                      <td colSpan={4} className="text-right px-3 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[#71717A]">Genel Toplam</td>
                      <td data-testid="return-grand-total" className="px-3 py-2.5 text-right font-finance font-medium">{TRY.format(sale.grand_total || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {sale.status === "completed" ? (
                <div className="bg-amber-50/40 border border-amber-200/60 p-4 sm:p-5">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-amber-800 mb-2">İade Sebebi</div>
                  <textarea
                    data-testid="return-reason"
                    value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Örn. Müşteri vazgeçti / hatalı seçim / hasar..."
                    rows={3}
                    className="w-full px-3 py-2.5 border border-[#E7E5E4] bg-white text-sm resize-none focus:border-[#C8A96A] outline-none"
                  />
                  <button
                    data-testid="return-confirm-btn"
                    onClick={confirmReturn} disabled={confirming || !reason.trim()}
                    className="mt-3 w-full bg-red-600 hover:bg-red-700 disabled:opacity-30 text-white text-sm uppercase tracking-[0.2em] py-4 min-h-[60px] inline-flex items-center justify-center gap-2.5 active:scale-[0.99] transition-transform"
                  >
                    {confirming ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                    <span>{confirming ? "İade ediliyor…" : "İadeyi Onayla"}</span>
                  </button>
                  <p className="mt-2 text-[10.5px] uppercase tracking-[0.15em] text-amber-800/80 text-center">
                    Bu işlem denetim defterine kaydedilir
                  </p>
                </div>
              ) : (
                <div className="bg-stone-50 border border-stone-200 p-4 text-sm text-stone-700 inline-flex items-center gap-2">
                  <AlertTriangle size={14} /> Bu satış zaten iade/iptal edilmiş. İade işlemi yapılamaz.
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA]">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}
